import { Box, useDisclosure } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'ui-components';
import { PlatformYouTubeColorIcon } from 'ui-components/dist/PlatformYouTubeColorIcon';
import {
  CONTENT_TYPES,
  SNACKBAR_ERROR_GENERIC_OPTIONS,
  SOCIAL_PLATFORMS,
} from '~/constants';
import { captureException } from '~/errorsLogging';
import { selectCreator } from '~/selectors/creator';
import {
  CreatorBenchmarks,
  DefaultMediaPackType,
  ValueOf,
  YoutubeInsightType,
  YoutubeType,
} from '~/types';

import { mediaPackContentToIds } from '../InstagramSection/InstagramSection.mappers';
import { MediaPackDetailSelectContentModal } from '../MediaPackDetailSelectContentModal';
import { UpdateContentSubmitValues } from '../MediaPackDetailSelectContentModal/MediaPackDetailSelectContentModal.types';
import { HighlightedContentSection } from '../MediaPacksDefaultTemplate/HighlightedContentSection';
import { OverviewSection } from '../MediaPacksDefaultTemplate/OverviewSection';
import { mapSubmitDataToUpdateMediaPackValues } from '../MediaPacksDefaultTemplate/OverviewSection/OverviewSection.mapper';
import {
  DEFAULT_YOUTUBE_METRIC_1,
  DEFAULT_YOUTUBE_METRIC_2,
  DEFAULT_YOUTUBE_METRIC_3,
} from './YoutubeSection.constants';
import {
  mapYoutubeContentToHighlightedContentProps,
  mapYoutubeMetricsToMetricCardData,
} from './YoutubeSection.mappers';

interface Props {
  mediaPack: DefaultMediaPackType;
  creatorBenchmarks: CreatorBenchmarks;
  ytContents: YoutubeType[];
  ytInsights?: YoutubeInsightType;
  onUpdateMediaPack: (submitValues: UpdateContentSubmitValues) => void;
  isSharedView?: boolean;
}

export const YoutubeSection: React.FC<Props> = ({
  mediaPack,
  creatorBenchmarks,
  ytInsights,
  ytContents,
  isSharedView,
  onUpdateMediaPack,
}) => {
  const { isOpen, onClose, onOpen } = useDisclosure();
  const creator = useSelector(selectCreator);
  const snackbar = useSnackbar();

  const {
    metric1 = DEFAULT_YOUTUBE_METRIC_1,
    metric2 = DEFAULT_YOUTUBE_METRIC_2,
    metric3 = DEFAULT_YOUTUBE_METRIC_3,
  } = mediaPack?.layout?.metricsPosition?.youtube || {};

  const metrics = metric1.concat(metric2, metric3);
  const reducedMetrics = mapYoutubeMetricsToMetricCardData(
    metrics,
    creatorBenchmarks,
  );

  const handleSubmit = async (
    selectedContent: YoutubeType[],
    selectedContentType: ValueOf<typeof CONTENT_TYPES>,
    platform: SOCIAL_PLATFORMS,
  ) => {
    const mediaPackValues = mapSubmitDataToUpdateMediaPackValues<YoutubeType>({
      selectedContent,
      selectedContentType,
      platform,
    });

    try {
      await onUpdateMediaPack(mediaPackValues);
      onClose();
    } catch (error) {
      snackbar.error(SNACKBAR_ERROR_GENERIC_OPTIONS);
      captureException(error);
    }
  };

  if (reducedMetrics.length === 0) {
    return null;
  }

  return (
    <Box>
      {!isSharedView && (
        <MediaPackDetailSelectContentModal
          initialSelectedItemIds={mediaPackContentToIds(ytContents)}
          contentType={CONTENT_TYPES.youtube}
          isOpen={isOpen}
          onCancel={onClose}
          onClose={onClose}
          onSubmit={handleSubmit}
          shouldShowSelectionPreview
          creatorId={mediaPack.creator.id}
        />
      )}

      {ytInsights && (
        <OverviewSection
          data-testid={`${SOCIAL_PLATFORMS.youtube}-overview-section`}
          overviewTitle="YouTube Overview"
          metrics={reducedMetrics}
          ages={ytInsights?.ages}
          countries={ytInsights?.countries?.slice(0, 3)}
          genders={ytInsights?.genders}
          icon={PlatformYouTubeColorIcon}
          creatorBenchmarks={creatorBenchmarks}
          mediaPack={mediaPack}
          platform={SOCIAL_PLATFORMS.youtube}
          isSharedView={isSharedView}
          insightsLastUpdatedAt={ytInsights.lastUpdatedAt}
        />
      )}

      <HighlightedContentSection
        marginTop={14}
        sectionTitle="YouTube Highlights"
        contents={mapYoutubeContentToHighlightedContentProps({
          hasManager: Boolean(creator?.manager?.id),
          isSharedView,
          ytContents,
        })}
        onEdit={onOpen}
        isSharedView={isSharedView}
      />
    </Box>
  );
};
